import { useStoredAgendaEvents } from '../modules/scheduling/portalAgendaEvents';
import { calendarDate, MonthCalendar } from '@giyus/patterns';
import { Card, CardContent, CardHeader, CardTitle, Button } from '@giyus/ui';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

/** Only manually recorded appointments in the authorized scope feed the Home calendar. */
export function HomeCalendar({ schedulingPath }: { readonly schedulingPath: string }) {
  const { t, i18n } = useTranslation('scheduling');
  const navigate = useNavigate();
  const { events } = useStoredAgendaEvents();
  const eventCounts: Record<string, number> = {};
  for (const event of events) eventCounts[event.dateIso] = (eventCounts[event.dateIso] ?? 0) + 1;
  const todayEvents = events
    .filter((event) => event.dateIso === calendarDate(new Date()))
    .sort((a, b) => a.startTime.localeCompare(b.startTime));
  return (
    <Card data-testid="home-month-calendar">
      <CardHeader>
        <CardTitle as="h2">{t('calendar.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <MonthCalendar
          eventCounts={eventCounts}
          locale={i18n.resolvedLanguage ?? 'pt-BR'}
          labels={{
            previousMonth: t('calendar.previousMonth'),
            nextMonth: t('calendar.nextMonth'),
            previousYear: t('calendar.previousYear'),
            nextYear: t('calendar.nextYear'),
            today: t('calendar.today'),
            dayEvents: (date, count) => t('calendar.selectDay', { date, count }),
          }}
          onSelectDate={(date) => {
            void navigate(`${schedulingPath}?tab=monthly_calendar&date=${date}`);
          }}
        />
        <div className="space-y-2">
          <h3 className="text-body-sm font-semibold">{t('calendar.todayAppointments')}</h3>
          {todayEvents.length === 0 ? (
            <p className="text-caption text-muted-foreground">{t('calendar.emptyToday')}</p>
          ) : (
            <ul className="space-y-2">
              {todayEvents.map((event) => (
                <li key={event.id}>
                  <Link
                    className="flex min-h-6 items-start gap-2 text-body-sm text-link underline-offset-4 hover:underline"
                    to={`${schedulingPath}?tab=monthly_calendar&date=${event.dateIso}`}
                  >
                    <span className="shrink-0 font-data">{event.startTime}</span>
                    <span className="min-w-0 break-words">{event.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
        <p className="text-caption text-muted-foreground">{t('calendar.openDayHint')}</p>
        <Button asChild variant="outline" className="w-full">
          <Link to={`${schedulingPath}?tab=monthly_calendar`}>{t('calendar.openSchedule')}</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
